import React, { useState, useEffect, useCallback } from 'react';
import CompaniesPanel from './CompaniesPanel';
import { UserCard, groupByUser } from './SparkUserCard';
import { Search, Monitor, Building2, Globe, X, Loader2, ShieldOff, History, RefreshCw, EyeOff, CheckCircle2, AlertTriangle, User } from 'lucide-react';

const fmt = (iso) => { try { return new Date(iso).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }); } catch (e) { return '—'; } };
const TRI = [{ v: null, l: 'Eredita' }, { v: true, l: 'Visibile' }, { v: false, l: 'Nascosto' }];
const SVC = [{ v: null, l: 'Eredita' }, { v: false, l: 'Attivo' }, { v: true, l: 'Oscurato' }];

const Seg = ({ options, value, onChange, prefix }) => (
    <div className="inline-flex bg-slate-950 border border-slate-800 rounded-lg p-0.5">
        {options.map(o => {
            const on = value === o.v;
            const tone = o.v === false && o.l === 'Nascosto' ? 'bg-red-600' : o.l === 'Oscurato' ? 'bg-red-600' : o.v === null ? 'bg-slate-700' : 'bg-emerald-600';
            return (
                <button key={o.l} type="button" onClick={() => onChange(o.v)} data-testid={`${prefix}-${o.l.toLowerCase()}`}
                    className={`px-2.5 py-1 text-xs rounded-md transition-colors ${on ? `${tone} text-white` : 'text-slate-400 hover:text-white'}`}>{o.l}</button>
            );
        })}
    </div>
);

const RuleEditor = ({ scope, target, title, buttons, existing, onClose, onSaved, api }) => {
    const [btn, setBtn] = useState(() => ({ ...(existing?.buttons || {}) }));
    const [blocked, setBlocked] = useState(existing?.blocked ?? null);
    const [msg, setMsg] = useState(existing?.block_message || '');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const save = async (clear) => {
        setSaving(true); setErr('');
        const body = clear ? { scope, target, buttons: {}, blocked: null } : { scope, target, buttons: btn, blocked, block_message: msg };
        const r = await api('/api/v1/admin/spark/rules', { method: 'PUT', body: JSON.stringify(body) });
        setSaving(false);
        if (r.ok) onSaved(); else setErr(r.data.detail || 'Salvataggio non riuscito');
    };
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="spark-rule-editor">
            <div className="w-full max-w-lg bg-slate-900 border border-slate-700 rounded-2xl p-5 max-h-[90vh] overflow-y-auto">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <p className="text-[11px] uppercase tracking-wider text-indigo-300">{scope === 'global' ? 'Regola globale' : scope === 'company' ? 'Regola azienda' : scope === 'user' ? 'Regola utente · tutti i suoi dispositivi' : 'Regola singolo dispositivo'}</p>
                        <h3 className="text-lg font-semibold text-white" data-testid="spark-rule-editor-title">{title}</h3>
                    </div>
                    <button onClick={onClose} className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800" data-testid="spark-rule-editor-close"><X className="w-5 h-5" /></button>
                </div>
                <p className="text-xs text-slate-500 mb-3">"Eredita" usa l'impostazione del livello superiore (Globale → Azienda → Utente → Dispositivo). Il livello più specifico vince.</p>
                <div className="space-y-2">
                    {buttons.map(b => (
                        <div key={b.key} className="flex items-center justify-between gap-3 py-1.5 border-b border-slate-800/70">
                            <span className="text-sm text-slate-200">{b.label}{b.default_hidden && <span className="ml-2 text-[10px] text-amber-300" data-testid={`spark-rule-default-hidden-${b.key}`}>nascosto di default</span>}</span>
                            <Seg options={TRI} value={btn[b.key] ?? null} onChange={v => setBtn(s => ({ ...s, [b.key]: v }))} prefix={`spark-rule-btn-${b.key}`} />
                        </div>
                    ))}
                </div>
                <div className="mt-5 p-3 rounded-xl border border-red-500/30 bg-red-500/5 space-y-2">
                    <div className="flex items-center justify-between gap-3">
                        <span className="text-sm font-medium text-red-200 flex items-center gap-2"><ShieldOff className="w-4 h-4" /> Servizio Spark</span>
                        <Seg options={SVC} value={blocked} onChange={setBlocked} prefix="spark-rule-service" />
                    </div>
                    {blocked === true && (
                        <textarea rows={3} value={msg} onChange={e => setMsg(e.target.value)} placeholder="Messaggio mostrato nella schermata Accesso Protetto (facoltativo)"
                            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-red-400 resize-none" data-testid="spark-rule-block-message" />
                    )}
                </div>
                {err && <p className="text-sm text-red-400 mt-3" data-testid="spark-rule-error">{err}</p>}
                <div className="flex gap-2 mt-5">
                    <button onClick={() => save(true)} disabled={saving} className="px-4 py-2.5 rounded-lg border border-slate-700 text-slate-300 hover:bg-slate-800 text-sm" data-testid="spark-rule-reset">Ripristina (eredita tutto)</button>
                    <button onClick={() => save(false)} disabled={saving} className="flex-1 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-sm font-medium flex items-center justify-center gap-2" data-testid="spark-rule-save">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva regola
                    </button>
                </div>
            </div>
        </div>
    );
};

const AuditList = ({ audit }) => (
    <div className="space-y-2" data-testid="spark-audit-list">
        {audit.length === 0 && <p className="text-sm text-slate-500">Nessuna modifica registrata.</p>}
        {audit.map((a, i) => {
            const after = a.after || {};
            const hidden = Object.entries(after.buttons || {}).filter(([, v]) => v === false).map(([k]) => k);
            const shown = Object.entries(after.buttons || {}).filter(([, v]) => v === true).map(([k]) => k);
            return (
                <div key={i} className="p-3 rounded-xl bg-slate-900 border border-slate-800 text-xs" data-testid={`spark-audit-row-${i}`}>
                    <div className="flex flex-wrap justify-between gap-2">
                        <span className="text-slate-200 font-medium">{({ rule_cleared: 'Regola ripristinata', install_removed: 'Spark rimosso', user_blocked: 'Utente bloccato', user_unblocked: 'Utente sbloccato', user_deleted: 'Utente eliminato', jira_import: 'Ticket importati da Jira', companies_rebuild: 'Aziende ricalcolate con AI', company_updated: 'Azienda modificata', company_merged: 'Aziende unite' })[a.action] || 'Regola aggiornata'} · <span className="text-indigo-300">{a.scope === 'global' ? 'Globale' : a.scope === 'company' ? 'Azienda' : a.scope === 'user' ? 'Utente' : 'Spark'}: {a.target}</span></span>
                        <span className="text-slate-500">{fmt(a.at)} · {a.admin}</span>
                    </div>
                    {a.action === 'rule_updated' && (
                        <p className="text-slate-400 mt-1">
                            {after.blocked === true && <span className="text-red-300">Servizio OSCURATO. </span>}
                            {after.blocked === false && <span className="text-emerald-300">Servizio attivo. </span>}
                            {hidden.length > 0 && <>Nascosti: {hidden.join(', ')}. </>}
                            {shown.length > 0 && <>Visibili: {shown.join(', ')}.</>}
                        </p>
                    )}
                </div>
            );
        })}
    </div>
);

export const SparkControlAdmin = ({ apiBase, getToken }) => {
    const [sub, setSub] = useState('installs');
    const [installs, setInstalls] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [rules, setRules] = useState([]);
    const [buttons, setButtons] = useState([]);
    const [audit, setAudit] = useState([]);
    const [query, setQuery] = useState('');
    const [company, setCompany] = useState('');
    const [loading, setLoading] = useState(false);
    const [editor, setEditor] = useState(null);
    const [notice, setNotice] = useState('');

    const api = useCallback(async (path, opts = {}) => {
        const r = await fetch(`${apiBase}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getToken()}` } });
        const data = await r.json().catch(() => ({}));
        return { ok: r.ok, data };
    }, [apiBase, getToken]);

    const load = useCallback(async () => {
        setLoading(true);
        const qs = new URLSearchParams({ query, company }).toString();
        const [i, r, b, a] = await Promise.all([api(`/api/v1/admin/spark/installs?${qs}`), api('/api/v1/admin/spark/rules'), api('/api/v1/admin/spark/buttons'), api('/api/v1/admin/spark/audit')]);
        if (i.ok) { setInstalls(i.data.installs || []); setCompanies(i.data.companies || []); }
        if (r.ok) setRules(r.data.rules || []);
        if (b.ok) setButtons(b.data.buttons || []);
        if (a.ok) setAudit(a.data.audit || []);
        setLoading(false);
    }, [api, query, company]);

    useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

    const findRule = (scope, target) => rules.find(r => r.id === (scope === 'global' ? 'global' : `${scope}:${scope === 'company' || scope === 'user' ? target.trim().toLowerCase() : target}`));
    const openEditor = (scope, target, title) => setEditor({ scope, target, title, existing: findRule(scope, target) });
    const onSaved = () => { setEditor(null); setNotice('Regola salvata: gli Spark la applicano entro 1 minuto.'); setTimeout(() => setNotice(''), 4000); load(); };
    const remove = async (d) => {
        if (!window.confirm(`Rimuovere il dispositivo "${d.computer || d.install_id}" dall'elenco? Se è ancora in uso ricomparirà al prossimo avvio di Spark.`)) return;
        await api(`/api/v1/admin/spark/installs/${encodeURIComponent(d.install_id)}`, { method: 'DELETE' });
        load();
    };
    const flash = (m) => { setNotice(m); setTimeout(() => setNotice(''), 4000); };
    const toggleBlock = async (d) => {
        const block = !d.user_blocked;
        if (!window.confirm(block ? `Bloccare ${d.email}? Non potrà accedere né aprire ticket, e i suoi Spark verranno oscurati.` : `Sbloccare ${d.email}?`)) return;
        const r = await api('/api/v1/admin/spark/users/block', { method: 'POST', body: JSON.stringify({ email: d.email, blocked: block }) });
        if (r.ok) flash(block ? `${d.email} bloccato.` : `${d.email} sbloccato.`);
        load();
    };
    const deleteUser = async (d) => {
        if (!window.confirm(`Eliminare ${d.email} da Spark? Verranno cancellati account, 2FA, passkey, dispositivi, regole e notifiche. Su Jira non viene toccato nulla. Operazione non reversibile.`)) return;
        const r = await api(`/api/v1/admin/spark/users/${encodeURIComponent(d.email)}`, { method: 'DELETE' });
        if (r.ok) flash(`${d.email} eliminato da Spark.`);
        load();
    };
    const online = installs.filter(d => d.online).length;
    const users = groupByUser(installs);
    const tabCls = (k) => `px-3 py-1.5 rounded-lg text-sm flex items-center gap-1.5 ${sub === k ? 'bg-slate-700 text-white' : 'text-slate-400 hover:text-white'}`;

    return (
        <main className="max-w-5xl mx-auto px-4 py-6 space-y-4" data-testid="spark-control-panel">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <div>
                    <h2 className="text-xl font-semibold text-white">Gestione Spark</h2>
                    <p className="text-sm text-slate-400" data-testid="spark-control-counts">{users.length} utenti · {installs.length} dispositivi · <span className="text-emerald-300">{online} online</span></p>
                </div>
                <div className="flex gap-2">
                    <button onClick={() => openEditor('global', '', 'Tutti gli Spark')} className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-200 hover:bg-slate-700 text-sm flex items-center gap-2" data-testid="spark-global-rule-btn"><Globe className="w-4 h-4" /> Regola globale</button>
                    <button onClick={load} className="p-2 rounded-lg bg-slate-800 border border-slate-700 text-slate-300 hover:bg-slate-700" title="Aggiorna" data-testid="spark-refresh-btn">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}</button>
                </div>
            </div>
            {notice && <p className="text-sm text-emerald-300 flex items-center gap-2" data-testid="spark-control-notice"><CheckCircle2 className="w-4 h-4" /> {notice}</p>}
            <div className="inline-flex bg-slate-900 border border-slate-800 rounded-xl p-1 gap-1">
                <button onClick={() => setSub('installs')} className={tabCls('installs')} data-testid="spark-sub-installs"><Monitor className="w-4 h-4" /> Spark registrati</button>
                <button onClick={() => setSub('rules')} className={tabCls('rules')} data-testid="spark-sub-rules"><EyeOff className="w-4 h-4" /> Regole attive ({rules.length})</button>
                <button onClick={() => setSub('companies')} className={tabCls('companies')} data-testid="spark-sub-companies"><Building2 className="w-4 h-4" /> Aziende</button>
                <button onClick={() => setSub('audit')} className={tabCls('audit')} data-testid="spark-sub-audit"><History className="w-4 h-4" /> Registro</button>
            </div>

            {sub === 'installs' && (
                <>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <div className="relative flex-1">
                            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
                            <input value={query} onChange={e => setQuery(e.target.value)} placeholder="Cerca nome, email, PC, azienda..." className="w-full bg-slate-900 border border-slate-800 rounded-lg pl-9 pr-3 py-2.5 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500" data-testid="spark-search-input" />
                        </div>
                        <select value={company} onChange={e => setCompany(e.target.value)} className="bg-slate-900 border border-slate-800 rounded-lg px-3 py-2.5 text-sm text-white" data-testid="spark-company-filter">
                            <option value="">Tutte le aziende</option>
                            {companies.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </div>
                    <div className="space-y-2" data-testid="spark-install-list">
                        {!loading && installs.length === 0 && <p className="text-sm text-slate-500 py-6 text-center">Nessuno Spark registrato. Compariranno qui al primo avvio.</p>}
                        {users.map(u => (
                            <UserCard key={u.key} u={u} api={api} onImported={load}
                                onUserRule={() => openEditor('user', u.email, `${u.name || u.email} · tutti i dispositivi`)}
                                onDeviceRule={(d) => openEditor('install', d.install_id, `${u.name || 'Utente'} · ${d.computer || d.device_type || ''}`)}
                                onCompanyRule={() => openEditor('company', u.company, u.company)}
                                onDeleteDevice={remove}
                                onBlock={() => toggleBlock({ email: u.email, user_blocked: u.blocked })}
                                onDeleteUser={() => deleteUser({ email: u.email })} />
                        ))}
                    </div>
                </>
            )}

            {sub === 'rules' && (
                <div className="space-y-2" data-testid="spark-rules-list">
                    {rules.length === 0 && <p className="text-sm text-slate-500">Nessuna regola: tutti gli Spark vedono tutti i pulsanti.</p>}
                    {rules.map(r => {
                        const hidden = Object.entries(r.buttons || {}).filter(([, v]) => v === false).map(([k]) => buttons.find(b => b.key === k)?.label || k);
                        const shown = Object.entries(r.buttons || {}).filter(([, v]) => v === true).map(([k]) => buttons.find(b => b.key === k)?.label || k);
                        const title = r.scope === 'global' ? 'Tutti gli Spark' : r.label || r.target;
                        return (
                            <div key={r.id} className="p-3 rounded-xl bg-slate-900 border border-slate-800 flex flex-wrap items-center justify-between gap-3" data-testid={`spark-rule-row-${r.id}`}>
                                <div className="text-sm">
                                    <p className="text-white font-medium flex items-center gap-2">
                                        {r.scope === 'global' ? <Globe className="w-4 h-4 text-indigo-300" /> : r.scope === 'company' ? <Building2 className="w-4 h-4 text-indigo-300" /> : r.scope === 'user' ? <User className="w-4 h-4 text-indigo-300" /> : <Monitor className="w-4 h-4 text-indigo-300" />}
                                        {title}
                                    </p>
                                    <p className="text-xs text-slate-400 mt-1">
                                        {r.blocked === true && <span className="text-red-300 font-medium">OSCURATO · </span>}
                                        {r.blocked === false && <span className="text-emerald-300">Servizio forzato attivo · </span>}
                                        {hidden.length > 0 && <>Nascosti: {hidden.join(', ')} </>}
                                        {shown.length > 0 && <>· Forzati visibili: {shown.join(', ')}</>}
                                    </p>
                                    <p className="text-[11px] text-slate-600 mt-0.5">{fmt(r.updated_at)} · {r.updated_by}</p>
                                </div>
                                <button onClick={() => setEditor({ scope: r.scope, target: r.scope === 'install' || r.scope === 'user' ? r.target : (r.label || r.target), title, existing: r })} className="px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white text-xs" data-testid={`spark-rule-edit-${r.id}`}>Modifica</button>
                            </div>
                        );
                    })}
                </div>
            )}

            {sub === 'companies' && <CompaniesPanel api={api} onChanged={load} />}
            {sub === 'audit' && <AuditList audit={audit} />}

            <p className="text-[11px] text-slate-600 flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Gli Spark controllano le regole ogni 60 secondi; un dispositivo è "online" se ha dato segni di vita negli ultimi 3 minuti, "inattivo" dopo 60 giorni senza segni di vita.</p>

            {editor && <RuleEditor {...editor} buttons={buttons} api={api} onClose={() => setEditor(null)} onSaved={onSaved} />}
        </main>
    );
};

export default SparkControlAdmin;
